import React, { useState, useEffect, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaBars,
  FaUser,
  FaSignOutAlt,
  FaShieldAlt,
  FaBell,
  FaShoppingBag,
} from "react-icons/fa";
import { getData } from "../../api/API";
import "./TopHeader.css";

const POLL_INTERVAL_MS = 10 * 60 * 1000; // refresh badge every 10 minutes

const TopHeader = ({
  toggleSidebar,
  userName = "User",
  userRole = "Admin",
  onLogout,
  companyLogo,
}) => {
  const navigate = useNavigate();
  const [newPoCount, setNewPoCount] = useState(0);
  const [recentPos, setRecentPos] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);

  const fetchPoNotifications = useCallback(async () => {
    try {
      const res = await getData("marketplace-orders");
      if (res?.data) {
        const orders = Array.isArray(res.data) ? res.data : [];
        // Only show orders that haven't had an invoice generated yet
        const pending = orders.filter(
          (o) => o.status === "PARSED" || o.status === "NEW"
        );
        setNewPoCount(pending.length);
        setRecentPos(pending.slice(0, 5));
      }
    } catch (_) {
      // Silently ignore — notification badge is non-critical
    }
  }, []);

  useEffect(() => {
    fetchPoNotifications();
    const interval = setInterval(fetchPoNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchPoNotifications]);

  const handleQuickNavigate = (path) => {
    navigate(path);
    setBellOpen(false);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const formatPoDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      });
    } catch (_) {
      return "";
    }
  };

  return (
    <header className="erp-top-header d-flex align-items-center justify-content-between">
      {/* Left Section: Brand & Sidebar Toggle */}
      <div className="d-flex align-items-center gap-3">
        <button
          className="erp-sidebar-toggle-btn"
          onClick={toggleSidebar}
          title="Toggle Navigation Sidebar"
          aria-label="Toggle Sidebar"
        >
          <FaBars />
        </button>

        <div className="erp-brand-container">
          {companyLogo ? (
            <img src={companyLogo} alt="Company Logo" className="erp-brand-logo" />
          ) : (
            <div className="erp-brand-name">SOOCHI</div>
          )}
        </div>
      </div>

      {/* Right Section */}
      <div className="erp-top-actions d-flex align-items-center gap-3">

        {/* PO Notification Bell */}
        <Dropdown
          align="end"
          show={bellOpen}
          onToggle={(isOpen) => setBellOpen(isOpen)}
        >
        <Dropdown.Toggle
  as="div"
  id="dropdown-po-notifications"
  className={`erp-bell-toggle position-relative ${
    newPoCount > 0 ? "has-notifications" : ""
  }`}
  style={{ cursor: "pointer" }}
  title="Purchase Order Notifications"
>
  <FaBell size={18} />
            {newPoCount > 0 && (
              <span
                className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                style={{ fontSize: "0.6rem", minWidth: "18px" }}
              >
                {newPoCount > 99 ? "99+" : newPoCount}
              </span>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu
            className="shadow-lg border-0 mt-2 p-0 rounded-3"
            style={{ minWidth: "320px" }}
          >
            {/* Bell Header */}
            <div className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between">
              <span className="fw-bold text-dark small d-flex align-items-center gap-2">
                <FaShoppingBag className="text-primary" size={13} />
                Purchase Orders
              </span>
              {newPoCount > 0 && (
                <span className="badge bg-danger-subtle text-danger border border-danger-subtle" style={{ fontSize: "0.65rem" }}>
                  {newPoCount} pending
                </span>
              )}
            </div>

            {/* PO List */}
            {recentPos.length === 0 ? (
              <div className="px-3 py-4 text-center text-muted small">
                <FaBell size={24} className="mb-2 opacity-25" />
                <div>No pending purchase orders</div>
              </div>
            ) : (
              recentPos.map((po) => (
                <div
                  key={po._id}
                  className="px-3 py-2 border-bottom erp-po-notification-item"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleQuickNavigate("/marketplaceorders")}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold text-dark" style={{ fontSize: "0.8rem" }}>
                        PO# {po.external_order_id}
                      </div>
                      <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                        {po.warehouse_name || po.retailer_id?.name || "—"}
                      </div>
                    </div>
                    <div className="text-end">
                      <span
                        className={`badge ${po.status === "PARSED" ? "bg-info-subtle text-info border border-info-subtle" : "bg-secondary-subtle text-secondary border border-secondary-subtle"}`}
                        style={{ fontSize: "0.62rem" }}
                      >
                        {po.status}
                      </span>
                      <div className="text-muted mt-1" style={{ fontSize: "0.68rem" }}>
                        {formatPoDate(po.order_date)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Footer */}
            <div
              className="px-3 py-2 text-center"
              style={{ cursor: "pointer" }}
              onClick={() => handleQuickNavigate("/marketplaceorders")}
            >
              <span className="text-primary small fw-semibold">
                View all Marketplace Orders →
              </span>
            </div>
          </Dropdown.Menu>
        </Dropdown>

        {/* User Profile */}
        <Dropdown align="end">
          <Dropdown.Toggle
            as="div"
            className="erp-user-profile"
            id="dropdown-user-profile"
          >
            <div className="erp-avatar">{getInitials(userName)}</div>
            <div className="erp-user-info d-none d-md-flex">
              <span className="erp-user-name">{userName}</span>
              <span className="erp-user-role">{userRole || "Administrator"}</span>
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu className="shadow-lg border-0 mt-2 p-2 rounded-3" style={{ minWidth: "200px" }}>
            <div className="px-3 py-2 border-bottom mb-2">
              <div className="fw-bold text-dark">{userName}</div>
              <div className="small text-muted d-flex align-items-center gap-1">
                <FaShieldAlt className="text-primary" size={12} /> {userRole || "User"}
              </div>
            </div>
            <Dropdown.Item
              onClick={() => handleQuickNavigate("/users")}
              className="rounded-2 py-2 d-flex align-items-center gap-2"
            >
              <FaUser className="text-muted" /> Profile Settings
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              onClick={onLogout}
              className="rounded-2 py-2 text-danger d-flex align-items-center gap-2"
            >
              <FaSignOutAlt /> Sign Out
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </header>
  );
};

export default TopHeader;
